import { FC, lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { PAGE_INFO_MAP } from '../../../constants/pageInfo';
import { useLoggedInUserFromContext } from '../../../contexts/loggedInUserContext';
import { useUrlBasedTitle } from '../../../hooks/useUrlBasedTitle/useUrlBasedTitle';
import { ROLE_ENUM } from '../../../types/roles';

const LoginContainer = lazy(() => import('../../auth/container/LoginContainer/LoginContainer'));
const AxiosService = lazy(() => import('../../../services/AxiosService/AxiosService'));
const AuthService = lazy(() => import('../../../services/AuthService/AuthService'));

const renderUnauthorizedNavigate = () => <Navigate to="/403" />;

export interface ProtectedLayoutProps {
  roleWhiteList?: ROLE_ENUM[],
  roleBlackList?: ROLE_ENUM[],
}

export const ProtectedLayout: FC<ProtectedLayoutProps> = ({
  roleWhiteList,
  roleBlackList,
}) => {
  const { pageInfo } = useUrlBasedTitle();
  useDocumentTitle(pageInfo?.title ?? PAGE_INFO_MAP.CARE_PORTAL.title);

  const { doesUserRoleIncludes, isUserLoggedIn, getCurrentRole } = useLoggedInUserFromContext();

  if (!isUserLoggedIn) {
    return <LoginContainer />;
  }

  if (!getCurrentRole()) {
    return renderUnauthorizedNavigate();
  }

  if (Array.isArray(roleWhiteList) && !doesUserRoleIncludes(roleWhiteList)) {
    return renderUnauthorizedNavigate();
  }

  if (Array.isArray(roleBlackList) && doesUserRoleIncludes(roleBlackList)) {
    return renderUnauthorizedNavigate();
  }

  return (
    <>
      <Outlet />
      <Suspense fallback={<div>loading...</div>}>
        <AxiosService />
        <AuthService />
      </Suspense>
    </>
  );
};

export default ProtectedLayout;
